// ═══════════════════════════════════════════════════════════
// I18nContext — інтернаціоналізація (6 мов: UK, EN, FR, ES, IT, PL)
// useT() → { t, lang, setLang, LANGUAGE_LIST }
// ═══════════════════════════════════════════════════════════
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import languages, { LANGUAGE_LIST } from '../i18n';

const I18nContext = createContext();

const STORAGE_KEY = 'app_language';
const DEFAULT_LANG = 'uk';

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && languages[stored] ? stored : DEFAULT_LANG;
  });

  const setLang = useCallback((code) => {
    if (languages[code]) {
      setLangState(code);
      localStorage.setItem(STORAGE_KEY, code);
    }
  }, []);

  const dict = languages[lang];
  const fallbackEn = languages['en'];
  const fallbackUk = languages[DEFAULT_LANG];

  const t = useCallback((key, fallbackOrParams, params) => {
    let str = dict[key] ?? fallbackEn[key] ?? fallbackUk[key];
    if (!str) {
      str = (typeof fallbackOrParams === 'string') ? fallbackOrParams : key;
    }
    const p = (typeof fallbackOrParams === 'object') ? fallbackOrParams : params;
    if (p) {
      for (const [k, v] of Object.entries(p)) {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      }
    }
    return str;
  }, [dict, fallbackEn, fallbackUk]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t,
    languages: LANGUAGE_LIST,
  }), [lang, setLang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used within I18nProvider');
  return ctx;
}
